import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Send, ShieldCheck, UserCheck, Landmark, Play, CheckCircle2 } from "lucide-react";

const STEPS = [
  { icon: Send, tag: "EMPLOYEE", title: "Request submitted", desc: "Delhi → Singapore · 4 days · Flights + Hotel" },
  { icon: ShieldCheck, tag: "POLICY ENGINE", title: "Policy auto-check passed", desc: "Tier L2 · Economy+ · Hotel cap $180/night" },
  { icon: UserCheck, tag: "MANAGER", title: "Approved in 1 tap", desc: "Budget APAC-Q3 · 62% remaining" },
  { icon: Landmark, tag: "FINANCE", title: "Posted to ERP", desc: "GL 6420-Travel · SAP cost centre synced" },
];

export const ApprovalSimulator = () => {
  const [step, setStep] = useState(-1);
  const [running, setRunning] = useState(false);

  const run = () => {
    setStep(-1);
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return;
    if (step >= STEPS.length - 1) {
      setRunning(false);
      return;
    }
    const t = setTimeout(() => setStep((s) => s + 1), step === -1 ? 500 : 1100);
    return () => clearTimeout(t);
  }, [running, step]);

  useEffect(() => {
    run();
  }, []);

  return (
    <div
      data-testid="hero-approval-simulator"
      className="rounded-xl border border-white/10 bg-slate-900/80 backdrop-blur-xl p-6 animate-pulse-glow"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-mono text-[11px] tracking-widest text-slate-400 uppercase">
            Live approval simulator
          </span>
        </div>
        <span className="font-mono text-[11px] text-slate-500">REQ-2481</span>
      </div>

      <div className="space-y-1">
        {STEPS.map((s, i) => {
          const done = i <= step;
          const active = i === step;
          return (
            <div key={s.title} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all duration-500 ${
                    done
                      ? "bg-blue-600/20 border-blue-500/50 text-blue-400"
                      : "bg-slate-800/60 border-white/5 text-slate-600"
                  }`}
                >
                  {done && !active ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <s.icon className="w-4 h-4" />
                  )}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-px h-8 transition-colors duration-500 ${i < step ? "bg-emerald-500/60" : "bg-white/10"}`} />
                )}
              </div>
              <div className={`pb-4 transition-opacity duration-500 ${done ? "opacity-100" : "opacity-35"}`}>
                <p className="font-mono text-[10px] tracking-widest text-blue-400/90">{s.tag}</p>
                <p className="text-sm font-semibold text-white mt-0.5">{s.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 h-1 rounded-full bg-slate-800 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-blue-600 to-emerald-500"
          animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <button
        data-testid="hero-simulate-run-button"
        onClick={run}
        disabled={running}
        className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-white/15 text-sm font-semibold text-white hover:bg-white/5 transition-colors duration-200 active:scale-[0.98] disabled:opacity-50"
      >
        <Play className="w-3.5 h-3.5" />
        {running ? "Simulating..." : "Run simulation again"}
      </button>
    </div>
  );
};
